// The cookie banner's language (docs/PORTAL_I18N.md area 9, CANDIDATE_I18N item 4).
//
// One banner is mounted for the whole site, outside every page's own i18n provider, so it
// picks its language from where the visitor is:
//   candidate pages (/careers, /interview, /i, /my-interviews) -> the candidate runtime's language
//   the native market pages (/de /fr /es /sv /it)                -> that language, from the portal catalogs
//   the native candidate pages (/ja /fil /th /id + /features/...)  -> that language, from the candidate catalogs
//   sign-in, sign-up and the portal (/auth, /dashboard)          -> the portal's language
//   anything else (the English site, blog, tools)                -> English
// It only reads: it never changes the portal's stored language or a candidate page's.

import { useEffect, useState } from "react";
import i18next from "i18next";
import { useLocation } from "react-router-dom";

import portalI18n, { UI_LANGUAGES } from "@/lib/i18n";
import { candidateI18n } from "@/lib/i18n/candidate";

const CANDIDATE_PATH = /^\/(careers|interview|i|my-interviews)(\/|$)/;
const PORTAL_PATH = /^\/(auth|dashboard)(\/|$)/;
const NATIVE_CODES = new Set(UI_LANGUAGES.map((l) => l.code).filter((code) => code !== "en"));
// Languages of the native candidate pages that the portal does not have.
const CANDIDATE_PAGE_CODES = new Set(["ja", "fil", "th", "id"]);
const catalogs = {
  ...import.meta.glob<Record<string, string>>("../../locales/ui/*.json", { import: "default" }),
  ...import.meta.glob<Record<string, string>>("../../locales/candidate/{ja,fil,th,id}.json", { import: "default" }),
};
const catalogPath = (code: string) =>
  CANDIDATE_PAGE_CODES.has(code) ? `../../locales/candidate/${code}.json` : `../../locales/ui/${code}.json`;

// Holds the native pages' catalogs, so the portal's own language is never touched.
const nativeI18n = i18next.createInstance();
nativeI18n.init({
  lng: "en",
  fallbackLng: false,
  resources: {},
  keySeparator: false,
  nsSeparator: false,
  returnEmptyString: false,
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
});

function nativeCode(pathname: string): string | null {
  const first = pathname.split("/")[1] || "";
  return NATIVE_CODES.has(first) || CANDIDATE_PAGE_CODES.has(first) ? first : null;
}

export function useConsentT(): (text: string) => string {
  const { pathname } = useLocation();
  const [, rerender] = useState(0);
  const native = nativeCode(pathname);

  useEffect(() => {
    const bump = () => rerender((n) => n + 1);
    portalI18n.on("languageChanged", bump);
    candidateI18n.on("languageChanged", bump);
    return () => {
      portalI18n.off("languageChanged", bump);
      candidateI18n.off("languageChanged", bump);
    };
  }, []);

  useEffect(() => {
    if (!native || nativeI18n.hasResourceBundle(native, "translation")) return;
    let alive = true;
    void catalogs[catalogPath(native)]?.().then((catalog) => {
      nativeI18n.addResourceBundle(native, "translation", catalog, true, true);
      if (alive) rerender((n) => n + 1);
    });
    return () => {
      alive = false;
    };
  }, [native]);

  if (CANDIDATE_PATH.test(pathname)) return (text) => candidateI18n.t(text);
  if (native) return (text) => nativeI18n.getFixedT(native)(text);
  if (PORTAL_PATH.test(pathname)) return (text) => portalI18n.t(text);
  return (text) => text;
}
